import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

import { orderAPI } from '../services/api'
import './UserProfile.css'
import { useDocumentMeta } from '../seo/useDocumentMeta'

function UserProfile() {
  useDocumentMeta()
  const location = useLocation()
  const { user, loading: authLoading, signOut, updateUserProfile } = useAuth()
  const [activeTab, setActiveTab] = useState('account')
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  
  // 编辑状态
  const [isEditing, setIsEditing] = useState(false)
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: ''
  })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  // 检查是否从其他页面跳转过来并指定了 tab
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab)
    }
  }, [location.state])

  // 切换 tab 时重置编辑状态
  useEffect(() => {
    setIsEditing(false)
    setSaveError(null)
    if (user) {
      setEditFormData({
        name: user.name || '',
        phone: user.phone || ''
      })
    }
  }, [activeTab, user])

  useEffect(() => {
    // 获取真实订单历史
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const result = await orderAPI.getMyOrders();
        
        console.log('Orders API result:', result);
        
        if (result.success) {
          console.log('Orders data:', result.data);
          setOrders(result.data || []);
        } else {
          console.error('Failed to fetch orders:', result.message);
          setOrders([]);
        }
      } catch (error) {
        console.error('Failed to fetch orders:', error);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    }

    if (user) {
      fetchOrders();
    }
  }, [user])

  // 初始化编辑表单数据
  useEffect(() => {
    if (user) {
      setEditFormData({
        name: user.name || '',
        phone: user.phone || ''
      })
    }
  }, [user])

  // 处理编辑按钮点击
  const handleEditClick = () => {
    setIsEditing(true)
    setSaveError(null)
  }

  // 处理取消编辑
  const handleCancelEdit = () => {
    setIsEditing(false)
    setSaveError(null)
    // 恢复原始数据
    if (user) {
      setEditFormData({
        name: user.name || '',
        phone: user.phone || ''
      })
    }
  }

  // 处理表单输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // 处理保存
  const handleSave = async () => {
    try {
      setSaving(true)
      setSaveError(null)

      const result = await updateUserProfile(editFormData)
      
      if (result.success) {
        setIsEditing(false)
        // 用户信息已通过 AuthContext 更新
      } else {
        setSaveError(result.error || 'Failed to update profile')
      }
    } catch (error) {
      console.error('Save profile error:', error)
      setSaveError(error.message || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  // AuthContext 是在 effect 里从 localStorage 还原 user 的，
  // 不等它就会在每次刷新时闪一下「请登录」
  if (authLoading) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="loading-container"><p>Loading...</p></div>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <h2>Please log in to view your profile</h2>
        </div>
      </div>
    )
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <div className="account-content">
            <h2>HELLO, {user.name?.toUpperCase() || 'USER'}</h2>
            
            <div className="section">
              <h3>PROFILE</h3>
              {isEditing ? (
                <div className="profile-edit-form">
                  <div className="form-group">
                    <label>Name</label>
                    <input 
                      type="text" 
                      name="name"
                      value={editFormData.name}
                      onChange={handleInputChange}
                      disabled={saving}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input 
                      type="email" 
                      value={user.email} 
                      disabled 
                      style={{ opacity: 0.6, cursor: 'not-allowed' }}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>Email cannot be changed</small>
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={editFormData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      disabled={saving}
                    />
                  </div>
                  {saveError && (
                    <div className="error-message" style={{ color: '#d32f2f', marginBottom: '10px', fontSize: '14px' }}>
                      {saveError}
                    </div>
                  )}
                  <div className="form-actions">
                    <button 
                      className="save-btn" 
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'SAVING...' : 'SAVE'}
                    </button>
                    <button 
                      className="cancel-btn" 
                      onClick={handleCancelEdit}
                      disabled={saving}
                    >
                      CANCEL
                    </button>
                  </div>
                </div>
              ) : (
                <>
              <div className="profile-info">
                <p><strong>Name:</strong> {user.name}</p>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Phone:</strong> {user.phone || 'Not provided'}</p>
              </div>
                  <button className="edit-btn" onClick={handleEditClick}>EDIT PROFILE</button>
                </>
              )}
            </div>
            
          </div>
        )
      
      case 'purchases':
        return (
          <div className="purchases-content">
            <h2>PURCHASES</h2>
            {loading ? (
              <div className="loading-container">
                <p>Loading orders...</p>
                <div className="loading-spinner"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="empty-state">
                <p>You have no purchase history.</p>
                <a href="/products" className="shop-now-btn">Start Shopping</a>
              </div>
            ) : (
              <div className="orders-list">
                {orders.map(order => (
                  <div key={order.orderNumber} className="order-item">
                    <div className="order-items-container">
                      {order.items.map((item, index) => (
                        <div key={index} className="order-item-row">
                          <div className="order-image-section">
                            {index === 0 && (
                              <div className={`order-status-text status-${order.status.toLowerCase()}`}>
                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                              </div>
                            )}
                            <img 
                              src={item.images && item.images.length > 0 ? item.images[0] : item.image || ''} 
                              alt={item.name} 
                              className="order-product-image" 
                            />
                          </div>
                          <div className="order-product-info">
                            <h3 className="order-product-name">{item.name}</h3>
                            <div className="order-product-details">
                              <span className="order-product-qty">Qty: {item.quantity}</span>
                              {item.size && <span className="order-product-size">Size: {item.size}</span>}
                              {item.sku && <span className="order-product-style">Style: {item.sku}</span>}
                            </div>
                          </div>
                          <div className="order-product-price">
                            ${item.price.toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="order-footer">
                      <div className="order-meta">
                        <span className="order-number">Order #{order.orderNumber}</span>
                        <span className="order-date">{new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span className="order-total">Total: ${order.pricing.total.toFixed(2)}</span>
                      </div>
                      <Link to={`/order/${order.orderNumber}`} className="view-details-btn">View Details</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      
      case 'profile':
        return (
          <div className="profile-settings-content">
            <h2>PROFILE SETTINGS</h2>
            {isEditing ? (
              <div className="profile-form">
                <div className="form-group">
                  <label>Name</label>
                  <input 
                    type="text" 
                    name="name"
                    value={editFormData.name}
                    onChange={handleInputChange}
                    disabled={saving}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input 
                    type="email" 
                    value={user.email || ''} 
                    disabled 
                    style={{ opacity: 0.6, cursor: 'not-allowed' }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>Email cannot be changed</small>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    disabled={saving}
                  />
                </div>
                {saveError && (
                  <div className="error-message" style={{ color: '#d32f2f', marginBottom: '10px', fontSize: '14px' }}>
                    {saveError}
                  </div>
                )}
                <div className="form-actions">
                  <button 
                    className="save-btn" 
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'SAVING...' : 'SAVE'}
                  </button>
                  <button 
                    className="cancel-btn" 
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            ) : (
            <div className="profile-form">
              <div className="form-group">
                <label>Name</label>
                <input type="text" value={user.name || ''} readOnly />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={user.email || ''} readOnly />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" value={user.phone || ''} readOnly />
              </div>
                <button className="edit-btn" onClick={handleEditClick}>EDIT PROFILE</button>
            </div>
            )}
          </div>
        )
      
      default:
        return null
    }
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-sidebar">
          <nav className="profile-nav">
            <button 
              className={`nav-item ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => setActiveTab('account')}
            >
              Account
            </button>
            <button 
              className={`nav-item ${activeTab === 'purchases' ? 'active' : ''}`}
              onClick={() => setActiveTab('purchases')}
            >
              Purchases
            </button>
            <button 
              className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </button>
          </nav>
        </div>
        
        <div className="profile-main">
          {renderContent()}
        </div>
      </div>
    </div>
  )
}

export default UserProfile